/**
 * content-stopped.js
 */

import React from 'react';
import { alpha } from '@mui/material';
import { useHistory } from 'react-router-dom';
import analytics from 'utils/analytics';
import { makeLocation } from 'utils/routing';
import NetworkConnectionContext from 'contexts/network-connection/network-connection.context';
import ConfirmDialog from 'components/confirm-dialog/confirm-dialog';
import FeatureBox from 'components/feature-box';
import DetailButton from 'components/detail-button';
import { TRACK_TYPE, TRACK_COMPLETION_ACTION } from '../tracking-common';
import { LiveTrackingContext } from '../live-tracking/live-tracking-context';
import { TrackingFormContext } from '../live-tracking/tracking-form-context';
import { TrackingDrawerContext } from './drawer-context';


// -----------------------------------------------------------------------------------------
// Complete Button
// -----------------------------------------------------------------------------------------


const CompleteButtonTitle = {
  [TRACK_TYPE.DEPLOYMENT]: 'COMPLETE DEPLOYMENT',
  [TRACK_TYPE.FOLLOW]: 'COMPLETE EXERCISE'
};


const CompleteButtonDescription = {
  [TRACK_TYPE.DEPLOYMENT]: 'Save this track and complete the deployment record now by adding your comments and additional information.',
  [TRACK_TYPE.FOLLOW]: 'Save this track and complete the exercise record now by adding your comments and additional information.'
};


const CompleteButtonLocationParams = {
  [TRACK_TYPE.DEPLOYMENT]: {
    sectionName: 'deployment',
    itemName: 'edit',
    params: (deployment) => ({ deploymentId: deployment.id, viewSectionId: 0, editSectionId: 0 })
  },
  [TRACK_TYPE.FOLLOW]: {
    sectionName: 'training',
    itemName: 'editAnswer',
    params: (completion) => ({ exerciseId: completion.exerciseId, answerId: completion.id })
  }
};


const CompleteButton = (props) => {
  const history = useHistory();
  const { isConnected } = React.useContext(NetworkConnectionContext);
  const { activeTrack, completeActiveTrack, resetTracking, setIsTrackingOpen } = React.useContext(LiveTrackingContext);
  const { setIsDrawerLoading } = React.useContext(TrackingDrawerContext);
  const { trackType } = React.useContext(TrackingFormContext);


  return (
    <DetailButton
      iconId='checkmark-outline'
      title={CompleteButtonTitle[trackType]}
      description={<>
        {CompleteButtonDescription[trackType]}
        {!isConnected && <><br/><br/>OFFLINE</>}
      </>}
      color='info'
      variant='contained'
      disabled={!isConnected}
      onClick={async () => {
        analytics.trackGA4('event', { name: 'track_stop', params: { trackstop_rec_type: trackType, trackstop_action: 'complete' } });

        try {
          setIsDrawerLoading(true);
          await completeActiveTrack();

          // nav to relevant record editor
          const basePath = history.location.pathname.startsWith('/records')
            ? history.location.pathname
            : '/records';
          const locationParams = CompleteButtonLocationParams[trackType];
          let params = null;
          if (activeTrack.type === TRACK_TYPE.DEPLOYMENT) {
            params = locationParams.params(activeTrack.deployment);
          } else if (activeTrack.type === TRACK_TYPE.FOLLOW) {
            params = locationParams.params(activeTrack.completion);
          }
          
          setIsTrackingOpen(false);
          resetTracking(TRACK_COMPLETION_ACTION.COMPLETE);

          history.push(makeLocation(
            basePath,
            locationParams.sectionName,
            locationParams.itemName,
            params,
            history.location,
          ));
        } catch {
          // TODO: indicate save failure
        } finally {          
          setIsDrawerLoading(false);
        }
      }}
    />
  );
}




// -----------------------------------------------------------------------------------------
// Save Button
// -----------------------------------------------------------------------------------------


const SaveButtonTitle = {
  [TRACK_TYPE.DEPLOYMENT]: 'SAVE FOR LATER',
  [TRACK_TYPE.LEAD]: 'SAVE TRACK',
  [TRACK_TYPE.FOLLOW]: 'SAVE FOR LATER'
}


const SaveButtonDescription = {
  [TRACK_TYPE.DEPLOYMENT]: 'Save this track and complete the deployment record at a later time.',
  [TRACK_TYPE.LEAD]: 'Save this track so that a K9 team can follow it later. They must be within 500 yards to see it.',
  [TRACK_TYPE.FOLLOW]: 'Save this track and complete the exercise record at a later time.'
}


const SaveButton = (props) => {
  const { completeActiveTrack, resetTracking } = React.useContext(LiveTrackingContext);
  const { setIsDrawerLoading } = React.useContext(TrackingDrawerContext);
  const { trackType } = React.useContext(TrackingFormContext);

  return (
    <DetailButton
      iconId='save'
      title={SaveButtonTitle[trackType]}
      description={SaveButtonDescription[trackType]}
      color='info'
      variant='contained'
      onClick={async () => {
        analytics.trackGA4('event', { name: 'track_stop', params: { trackstop_rec_type: trackType, trackstop_action: 'save' } });

        try {
          setIsDrawerLoading(true);
          await completeActiveTrack();
          resetTracking(TRACK_COMPLETION_ACTION.SAVE);
        } catch {
          // TODO: indicate that the save failed.
        } finally {
          setIsDrawerLoading(false);
        }
      }}
    />
  );
}



// -----------------------------------------------------------------------------------------
// Discard Button
// -----------------------------------------------------------------------------------------


const DiscardButton = (props) => {
  const { discardActiveTrack, resetTracking } = React.useContext(LiveTrackingContext);
  const { setIsDrawerLoading } = React.useContext(TrackingDrawerContext);
  const { trackType } = React.useContext(TrackingFormContext);
  const [confirm, setConfirm] = React.useState();

  return <>
    <DetailButton
      iconId='delete'
      title='DISCARD TRACK'
      sx={{ 
        backgroundColor: (theme) => alpha(theme.palette.error.light, 0.4),
        color: 'error.dark'
      }}
      onClick={async () => {
        if (await confirm()) {
          analytics.trackGA4('event', { name: 'track_stop', params: { trackstop_rec_type: trackType, trackstop_action: 'discard' } });

          try {
            setIsDrawerLoading(true);
            // if discard fails a toast will be displayed.
            await discardActiveTrack();
          } finally {
            // we reset even if discard fails because the remaining options no longer make sense.
            resetTracking(TRACK_COMPLETION_ACTION.DISCARD);
            setIsDrawerLoading(false);
          }
        }
      }}
    />
    {/* CONFIRM DIALOG */}
    <ConfirmDialog
      titleStyle='warning'
      title='Confirm Discard Track'
      titleIcon='warning'
      setConfirm={setConfirm}
      content='Are you sure you want to discard the track?'
      customActions={[
        {
          label: 'Cancel',
          value: false,
          isNotPriority: true,
        },
        {
          label: 'Discard',
          value: true,
        }
      ]}
    />    
  </>;
}



// -----------------------------------------------------------------------------------------
// Track Type Actions & Content Component
// -----------------------------------------------------------------------------------------


const SaveCompleteButtons = (props) => {
  const { activeTrack } = React.useContext(LiveTrackingContext);
  const isPersisted = activeTrack.id 
    && activeTrack.markers.filter(m => !m.id).length === 0 
    && activeTrack.persistedPointCount === activeTrack.collectedPointCount;

  return (
    <FeatureBox loading={!isPersisted}>
      {props.children}
    </FeatureBox>
  )
}


const ActionsDeployment = (props) => {  
  return (
    <>
      <SaveCompleteButtons>
        <CompleteButton />
        <SaveButton />
      </SaveCompleteButtons>
      <DiscardButton />
    </>
  );
}


const ActionsLead = (props) => {
  return (
    <>
      <SaveCompleteButtons>
        <SaveButton />
      </SaveCompleteButtons>
      <DiscardButton />
    </>
  );
}


const ActionsFollow = (props) => {
  return (
    <>
      <SaveCompleteButtons>
        <CompleteButton />
        <SaveButton />
      </SaveCompleteButtons>
      <DiscardButton />
    </>
  );
}


const ACTIONS = {
  [TRACK_TYPE.DEPLOYMENT]: <ActionsDeployment />,
  [TRACK_TYPE.LEAD]: <ActionsLead />,
  [TRACK_TYPE.FOLLOW]: <ActionsFollow />,
};


export default function ContentStopped(props) {
  const { trackType } = React.useContext(TrackingFormContext);

  return (
    <>
      {ACTIONS[trackType]}
    </>
  );
}